import React, { memo, useMemo } from "react";
import { Card } from "@/shared";
import {
  buildAnalyticsSections,
  formatMoney,
  formatMonth,
  formatNumber,
  formatPercent,
  formatPerMile,
} from "./analyticsFormatters";

/**
 * Small two-column-or-more table used for the breakdowns.
 * @param {object} props
 * @param {string[]} props.headers
 * @param {Array<Array<React.ReactNode>>} props.rows
 */
export const BreakdownTable = ({ headers, rows }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
          {headers.map((header, index) => (
            <th
              key={header}
              className={`py-2 px-2 font-semibold ${index > 0 ? "text-right" : ""}`}
            >
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((cells, rowIndex) => (
          <tr key={rowIndex} className="border-b border-gray-100 last:border-0">
            {cells.map((cell, cellIndex) => (
              <td
                key={cellIndex}
                className={`py-1.5 px-2 ${
                  cellIndex > 0 ? "text-right tabular-nums" : "text-gray-800"
                }`}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * InvoiceAnalyticsPanel
 * Shows the backend-computed invoice analytics (collections, charge mix,
 * fuel surcharge averages, per-mile operations, aging, and client/status/month
 * breakdowns) above the invoice report table.
 *
 * Renders nothing when `analytics` is missing, so older saved reports are unaffected.
 *
 * @param {object} props
 * @param {object} [props.analytics] - `response.analytics` from `POST /api/v1/reports`
 * @returns {JSX.Element|null}
 */
export const InvoiceAnalyticsPanel = ({ analytics }) => {
  const sections = useMemo(() => buildAnalyticsSections(analytics), [analytics]);

  if (!analytics?.summary || !analytics.summary.invoiceCount) return null;

  const { summary, aging = [], byClient = [], byStatus = [], byMonth = [] } =
    analytics;

  const kpis = [
    { label: "Gross", value: formatMoney(summary.gross) },
    { label: "Outstanding", value: formatMoney(summary.outstanding) },
    { label: "Overdue", value: formatMoney(summary.overdue), alert: summary.overdue > 0 },
    { label: "Collection rate", value: formatPercent(summary.collectionRate) },
    { label: "Fuel % of base", value: formatPercent(summary.fuel?.percentOfBase) },
    { label: "Revenue / mile", value: formatPerMile(summary.operations?.revenuePerMile) },
  ];

  return (
    <div className="space-y-4 mb-4">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {kpis.map((kpi) => (
          <Card key={kpi.label}>
            <div className="text-xs text-gray-500">{kpi.label}</div>
            <div
              className={`text-lg font-semibold tabular-nums ${
                kpi.alert ? "text-red-600" : "text-gray-900"
              }`}
            >
              {kpi.value}
            </div>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {sections.map((section) => (
          <Card key={section.id} title={section.title}>
            <dl className="text-sm divide-y divide-gray-100">
              {section.rows.map((row) => (
                <div key={row.label} className="flex justify-between py-1">
                  <dt className="text-gray-600">{row.label}</dt>
                  <dd className="font-medium tabular-nums">{row.value}</dd>
                </div>
              ))}
            </dl>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {aging.length > 0 && (
          <Card title="Outstanding aging">
            <BreakdownTable
              headers={["Bucket", "Invoices", "Amount"]}
              rows={aging.map((bucket) => [
                bucket.label,
                formatNumber(bucket.count),
                formatMoney(bucket.amount),
              ])}
            />
          </Card>
        )}
        {byStatus.length > 0 && (
          <Card title="By status">
            <BreakdownTable
              headers={["Status", "Invoices", "Amount"]}
              rows={byStatus.map((row) => [
                row.status,
                formatNumber(row.count),
                formatMoney(row.amount),
              ])}
            />
          </Card>
        )}
        {byMonth.length > 0 && (
          <Card title="By month">
            <BreakdownTable
              headers={["Month", "Invoices", "Gross", "Outstanding"]}
              rows={byMonth.map((row) => [
                formatMonth(row.month),
                formatNumber(row.invoiceCount),
                formatMoney(row.gross),
                formatMoney(row.outstanding),
              ])}
            />
          </Card>
        )}
      </div>

      {byClient.length > 0 && (
        <Card title="By client">
          <BreakdownTable
            headers={[
              "Client",
              "Invoices",
              "Gross",
              "Paid",
              "Outstanding",
              "Overdue",
              "Collected",
              "Avg invoice",
              "Fuel % of base",
              "Miles",
              "Rev / mile",
            ]}
            rows={byClient.map((row) => [
              row.clientName,
              formatNumber(row.invoiceCount),
              formatMoney(row.gross),
              formatMoney(row.paid),
              formatMoney(row.outstanding),
              formatMoney(row.overdue),
              formatPercent(row.collectionRate),
              formatMoney(row.averageInvoice),
              formatPercent(row.fuelPercentOfBase),
              formatNumber(row.miles),
              formatPerMile(row.revenuePerMile),
            ])}
          />
        </Card>
      )}
    </div>
  );
};

export default memo(InvoiceAnalyticsPanel);
